// BL-177 Step 2i: which build of the push worker is actually in control on this device, whether it is the one this page expects, and a
// banner test that takes the push service out of the picture. A worker that never reported back, or a banner that never shows, can then
// be told apart: old worker still running / push not arriving / the browser or the Mac refusing to show a banner.

/** Must equal WORKER_VERSION in public/push-worker.js (a test checks), so the card can say when an older worker is still running. */
export const EXPECTED_WORKER_VERSION = '2026-10-15.2i';

async function activeRegistration() {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return null;
    return (await navigator.serviceWorker.getRegistration()) || null;
}

/** Asks the running worker for its version. Resolves '' when no worker answers (none installed, or an older one that does not know the question). */
export async function readWorkerVersion(registration = null, timeoutMs = 1500) {
    try {
        const reg = registration || await activeRegistration();
        const worker = reg && reg.active;
        if (!worker || typeof MessageChannel !== 'function') return '';
        return await new Promise((resolve) => {
            const channel = new MessageChannel();
            const timer = setTimeout(() => resolve(''), timeoutMs);
            channel.port1.onmessage = (event) => { clearTimeout(timer); resolve(typeof event.data?.version === 'string' ? event.data.version : ''); };
            worker.postMessage({ type: 'ia-worker-version' }, [channel.port2]);
        });
    } catch {
        return '';
    }
}

/** Checks for a newer worker file now (the browser would otherwise wait up to a day); a new one takes over at once (skipWaiting). */
export async function checkWorkerUpdate() {
    try {
        const reg = await activeRegistration();
        if (reg && typeof reg.update === 'function') await reg.update();
    } catch { /* offline, or no worker yet */ }
}

/** The line under "This device": which worker is running. */
export function workerVersionLine(running, expected = EXPECTED_WORKER_VERSION) {
    if (!running) return 'Push worker: not answering, or an older one is still running. Close this window or app and open it again.';
    if (running !== expected) return `Push worker: ${running}, an older build (this page has ${expected}). It updates on its own; if it stays, close the app and open it again.`;
    return `Push worker: ${running} (up to date)`;
}

/**
 * Shows a banner straight from this page (no push involved). If it appears, showing banners works here and any missing banner is on the way
 * (push service, worker). If it does not, the browser or the Mac is refusing banners. Never throws.
 * @returns {{ ok: boolean, listed: boolean|null, permission: string, error: string }}
 */
export async function showLocalTestBanner(title = 'Test banner', body = 'This banner was shown by this page, not by a push.') {
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
    try {
        const reg = await activeRegistration();
        if (!reg || typeof reg.showNotification !== 'function') return { ok: false, listed: null, permission, error: 'No notification worker is installed on this device yet. Turn alerts on first.' };
        await reg.showNotification(title, { body, tag: 'ia-local-test', data: { url: '/my-notifications' } });
        let listed = null;
        try { listed = (await reg.getNotifications({ tag: 'ia-local-test' })).length > 0; } catch { listed = null; }
        return { ok: true, listed, permission, error: '' };
    } catch (failure) {
        return { ok: false, listed: null, permission, error: (failure && (failure.message || String(failure))) || 'The browser refused to show a banner.' };
    }
}

/** What the result of showLocalTestBanner means, in plain words, with what to check when nothing appears. */
export function localBannerAdvice(result, { platform = 'other', browserName = 'this browser' } = {}) {
    if (!result) return '';
    if (!result.ok) {
        return `${browserName} refused to show the banner: ${result.error} ${result.permission && result.permission !== 'granted' ? `(Notification permission is "${result.permission}".)` : ''}`.trim();
    }
    const mac = platform === 'mac'
        ? ` On a Mac, open System Settings › Notifications › ${browserName} and make sure Allow notifications is on and the style is Banners or Alerts, and turn off any Focus that is on.`
        : '';
    return `The banner was handed to ${browserName}${result.listed === false ? ', but it is not in its list of shown notifications' : ''}. If you did not see it appear, ${browserName} or the system is hiding it, not IntelAgent.${mac}`;
}
